/**
 * Client theming that is read in more than one place.
 *
 * config["colors"]["btn"] has always set the fill of every primary button, while
 * the lettering on it was hardcoded white -- in a stylesheet rule for some
 * screens and in an inline style for others. A pale fill leaves white lettering
 * unreadable (one live config ships #A3E6E9), so the foreground is configurable
 * too.
 */

/** What every button used before this was configurable. */
export const BTN_TEXT_FALLBACK = "#ffffff";

/**
 * The configured button text colour, or white.
 *
 * Read from colors.btnText, or colors["btn-text"] -- the colours block mixes
 * camelCase and kebab-case keys already (btn, secoundaryBtn, incomplete-block),
 * so both spellings are accepted rather than making the client guess.
 */
export const btnTextColor = (config) => {
  const colors = (config && config["colors"]) || {};
  return colors["btnText"] || colors["btn-text"] || BTN_TEXT_FALLBACK;
};

/**
 * Publish it as a custom property on <html>.
 *
 * Buttons live in stylesheet rules, in inline styles, and inside overlays that
 * render outside the app tree; one property on the document element reaches all
 * of them through var(--btn-text, #fff), which is also why every reader states
 * white as the var fallback: before a config arrives, and for any client that
 * sets no text colour, nothing changes.
 */
export const applyBtnTextColor = (config) => {
  try {
    document.documentElement.style.setProperty(
      "--btn-text",
      btnTextColor(config)
    );
    return true;
  } catch (e) {
    // No DOM to write to: the var() fallbacks keep every button white.
    return false;
  }
};

/**
 * The client's text colour from colors.text, or null when it sets none.
 *
 * It replaces the app's dark body text (headings, labels, module names, popup
 * copy). Button text has its own key above; white text over the camera, grey
 * hints, red errors and green "done" states keep their colours so they still
 * read and still mean something.
 */
export const textColor = (config) => {
  const colors = (config && config["colors"]) || {};
  return colors["text"] || null;
};

/**
 * Publish it as --app-text on <html>. Every dark text colour in the app is
 * written as var(--app-text, <old colour>), so with no colors.text the property
 * is removed and every one of them falls back to what it was.
 */
export const applyTextColor = (config) => {
  try {
    const color = textColor(config);
    if (color) document.documentElement.style.setProperty("--app-text", color);
    else document.documentElement.style.removeProperty("--app-text");
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Ink for an icon drawn on the client's navbar colour: dark or light by its
 * brightness. Anything that isn't a hex colour gets white, which suits the app's
 * default dark navbar.
 */
export const inkOn = (color) => {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color || "");
  if (!match) return "#ffffff";
  let hex = match[1];
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#1a1a1a" : "#ffffff";
};

/**
 * Outline of a module / section box that is not done yet: the client's text
 * colour when it has one, so names and outlines match, else incomplete-block.
 * Done boxes keep complete-block (green).
 */
export const incompleteBlockColor = (config) => {
  const colors = (config && config["colors"]) || {};
  return textColor(config) || colors["incomplete-block"];
};
